import React from 'react'
import { Link } from 'gatsby'

import {
  Layout,
  Showcase,
  SectionTitle,
  Header,
  Intro,
} from '../../../../components'

import './style.scss'
import data from './data.json'
import pic from '../../../../images/paris.jpg'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      />
      <Layout
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      >
        <Intro>
          <p>
            Cards collect discrete information within a container. A single card
            may be <b>standalone</b> in order to create a visual distinction
            between information within the card and the rest of the information
            on the page, or multiple cards may be used in a <b>collection</b> to
            group related pieces of information. Cards provide a snapshot and
            should encourage users to click through for futher action.
          </p>
        </Intro>
        <SectionTitle>Structure</SectionTitle>
        <p>
          The card pattern is customizable to a number of scenarios and should
          use the design that will create the optimal experience. All cards in
          one collection should have the same basic layout and component parts.
        </p>
        <p>
          The only required elements within a container are the title and body
          content. The optional elements listed below provide additional
          information and visual prominence where necessary.
        </p>
        <ul class="c-unordered-list l-margin-vertical--large">
          <li class="c-unordered-list__item l-margin-left--medium">
            The <b>container</b> creates the barrier for the card information.
            It separates a card from other in a collection or, if standalone,
            from the rest of the elements on the page.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            An <b>image</b> or <b>yellow border bar</b> may be used as a
            spotlight to draw attention to a card on a busy page. Do not use
            both an image and a border bar.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            The <b>eyebrow text</b> is used to give secondary or supporting
            information. Eyebrows use the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/title/develop'}
            >
              title 4
            </Link>{' '}
            text style.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            The <b>Title</b> details what is contained in the card body. Titles
            use the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/title/develop'}
            >
              title 2
            </Link>{' '}
            text style.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            <b>Body content</b> expands on the card title and uses the default{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/text-content/develop'}
            >
              text content
            </Link>{' '}
            variation.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            Cards may have up to two associated <b>actions</b> in the form of{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/button/develop'}
            >
              buttons
            </Link>{' '}
            and{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/link/develop'}
            >
              links
            </Link>{' '}
            . A card may only use a primary button if it is standalone and
            offers the primary action on the page. Otherwise, cards with two
            actions must use a horizontally stacked default button and text
            link.
          </li>
        </ul>
        <SectionTitle>Variations</SectionTitle>
        <Showcase
          site={data.site}
          title="Standard"
          text="Add and remove elements and classes to customize the attention card to best suit your design. The card container will respond as elements are added and removed."
          code={code.StandardCard}
        >
          <div className="card-wrapper--small">
            <div class="c-card">
              <img class="c-card__img" src={pic} alt="Card" />
              <div class="c-card__body">
                <div class="c-card__eyebrow">Eyebrow Text</div>
                <h1 class="c-card__title">Header</h1>
                <p class="c-card__text">
                  Card body content is concise and immediately relevant to the
                  card's header.
                </p>
                <div class="c-card__button-container">
                  <button class="c-card__button--primary">Action 1</button>
                  <button class="c-card__button--default">Action 2</button>
                  <a class="c-card__button--link" href="/#">
                    Action 3
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Showcase>

        <Showcase
          full={true}
          site={data.site}
          title="Hero"
          text="The hero card variation is a larger, horizontal card that has all possible component parts. The image in a hero card is more prominent as it takes up 50% of the container width. Hero cards are only used as standalones and may only be used at desktop breakpoints. At smaller breakpoints, they convert to standard cards."
          code={code.HeroCard}
        >
          <div className="card-wrapper">
            <div class="c-card--hero">
              <img class="c-card__img--hero-mobile" src={pic} alt="Card" />
              <div class="c-card__body--hero">
                <div class="c-card__eyebrow">Eyebrow Text</div>
                <h1 class="c-card__title--hero">Header</h1>
                <p class="c-card__text">
                  Card body content is concise and immediately relevant to the
                  card's header.
                </p>
                <div class="c-card__button-container--hero">
                  <button class="c-card__button--primary--hero">
                    Action 1
                  </button>
                  <button class="c-card__button--default--hero">
                    Action 2
                  </button>
                </div>
              </div>
              <img class="c-card__img--hero-desktop" src={pic} alt="Card" />
            </div>
          </div>
        </Showcase>
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          Card containers are flexible and margins should adapt to the card
          size. Use more spacing in between larger cards and unrelated layout
          elements around them. Use less spacing in between related cards in a
          collection.
        </p>
        <SectionTitle>Microcopy</SectionTitle>
        <p>
          Because of the space restraints inherent in cards, copy should be
          concise. The relationship between a card's body content, optional
          eyebrow, title and image should be immediately apparent. Follow the
          microcopy guidelines in{' '}
          <Link class="link" to={'/' + data.site + '/components/title/develop'}>
            titles
          </Link>
          ,{' '}
          <Link
            class="link"
            to={'/' + data.site + '/components/button/develop'}
          >
            buttons
          </Link>{' '}
          and{' '}
          <Link class="link" to={'/' + data.site + '/components/link/develop'}>
            links
          </Link>{' '}
          for more information.
        </p>
      </Layout>
    </>
  )
}

export const code = {
  StandardCard: `
<div class="c-card">
  <img class="c-card__img" src={pic} alt="Card" />
  <div class="c-card__body">
    <div class="c-card__eyebrow">Eyebrow Text</div>
    <h1 class="c-card__title">Header</h1>
    <p class="c-card__text">
      Card body content is concise and immediately relevant to the
      card's header.
    </p>
    <div class="c-card__button-container">
      <button class="c-card__button--primary">Action 1</button>
      <button class="c-card__button--default">Action 2</button>
      <a href="/#">Action 3</a>
    </div>
  </div>
</div>
`,
  SmallCard: `
<div class="c-card__border-top">
  <div class="c-card__body--small">
    <div class="c-card__title--small">Card Header</div>
    <p class="c-card__text--small">
      Card body content is concise and immediately relevant to the
      card's header.
    </p>   
    <button class="c-card__button--default">Action 1</button>
  </div>
</div>
`,
  HeroCard: `
<div class="c-card--hero">
  <img class="c-card__img--hero-mobile" src={pic} alt="Card" />
  <div class="c-card__body--hero">
    <div class="c-card__eyebrow">Eyebrow Text</div>
    <h1 class="c-card__title--hero">Header</h1>
    <p class="c-card__text">
      Card body content is concise and immediately relevant to the
      card's header.
    </p>
    <div class="c-card__button-container--hero">
      <button class="c-card__button--primary--hero">Action 1</button>
      <button class="c-card__button--default--hero">Action 2</button>
    </div>
  </div>
  <img class="c-card__img--hero-desktop" src={pic} alt="Card" />
</div>
`,
}
